import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocalization } from "@fluent/react";
import {
  Alert,
  Button,
  formatNumber,
  IconButton,
  useBreakpoints,
} from "@narmi/design_system";
import { format } from "date-fns";
import cc from "classcat";
import type { ChartDatum } from "../Charts/types";
import { useAccountsContext } from "../../contexts/AccountContext";
import SectionCard from "../../SectionCard";
import BorderCard from "../../BorderCard";
import AccountFilterPanel from "./AccountFilterPanel";
import { useCurrentUserNetWorthFilterStorage } from "./filterStorage";
import { getEligibleAccounts, useNetWorthTotals } from "./hooks";
import NetWorthBreakdown from "./NetWorthBreakdown";
import NetWorthGainsLosses from "./NetWorthGainsLosses";
import NetWorthPeriodTabs from "./NetWorthPeriodTabs";
import { useNetWorthAccountTrends, useNetWorthTrendData, type NetWorthPeriod } from "./TrendChart/hooks";
import NetWorthTrendChart from "./TrendChart/NetWorthTrendChart";
import styles from "./NetWorthWidgetCard.module.scss";

type NetWorthWidgetCardProps = {
  borderCard?: boolean;
};

type DrawerKind = "breakdown" | "filter" | "gainsLosses";

const NetWorthWidgetCard = ({ borderCard }: NetWorthWidgetCardProps) => {
  const { l10n } = useLocalization();
  const { m } = useBreakpoints();
  const { accounts } = useAccountsContext();
  const [selectedAccountIds, setSelectedAccountIds] =
    useCurrentUserNetWorthFilterStorage();
  const [period, setPeriod] = useState<NetWorthPeriod>("annual");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // Kept stable during the close transition, so content doesn't flip mid-slide.
  const [drawerKind, setDrawerKind] = useState<DrawerKind>("breakdown");
  const [selectedMonth, setSelectedMonth] = useState<Date | null>(null);
  const openDrawer = (kind: DrawerKind) => {
    setDrawerKind(kind);
    setIsDrawerOpen(true);
  };
  const openGainsLosses = (datum: ChartDatum) => {
    setSelectedMonth(datum.x);
    openDrawer("gainsLosses");
  };

  const eligibleAccounts = useMemo(
    () => getEligibleAccounts(accounts, []),
    [accounts]
  );
  const filteredAccounts = useMemo(
    () => getEligibleAccounts(accounts, selectedAccountIds),
    [accounts, selectedAccountIds]
  );
  const selectedAccounts = useMemo(() => {
    if (selectedAccountIds.length === 0) return [];
    const selected = new Set(selectedAccountIds);
    return eligibleAccounts.filter((account) => selected.has(account.id));
  }, [eligibleAccounts, selectedAccountIds]);
  const selectedInternalAccountIds = useMemo(
    () =>
      selectedAccounts
        .filter((account) => account.isInternal())
        .map((account) => account.id),
    [selectedAccounts]
  );
  const selectedExternalAccountIds = useMemo(
    () =>
      selectedAccounts
        .filter((account) => account.isExternal())
        .map((account) => account.id),
    [selectedAccounts]
  );

  const {
    totalAssets,
    totalLiabilities,
    netWorth,
    hasDisconnectedExternalAccount,
  } = useNetWorthTotals(selectedAccountIds);
  const {
    chartData,
    windowStart,
    windowEnd,
    changeThisMonth,
    externalTrackingStartedAt,
    availablePeriods,
    isLoading: isTrendLoading,
    error: trendError,
  } = useNetWorthTrendData(
    period,
    selectedInternalAccountIds,
    selectedExternalAccountIds,
    // selectedInternalAccountIds and selectedExternalAccountIds may both
    // come up empty while filtering over selectedAccountIds. by checking
    // selectedAccountIds directly we can identify if a filter is in use
    selectedAccountIds.length > 0
  );

  // "semiannual" is always treated as available, even if the backend omits
  // it (see NetWorthPeriodTabs).
  useEffect(() => {
    if (isTrendLoading || trendError) return;
    if (period === "semiannual" || availablePeriods.includes(period)) return;
    setPeriod(availablePeriods[availablePeriods.length - 1] ?? "semiannual");
  }, [availablePeriods, isTrendLoading, trendError, period]);
  // Only fetch while the gains/losses drawer is open - avoids a background
  // per-account refetch after the first click.
  const {
    data: accountTrends,
    isLoading: isAccountTrendsLoading,
    hasError: hasAccountTrendsError,
  } = useNetWorthAccountTrends(
    period,
    filteredAccounts,
    isDrawerOpen && drawerKind === "gainsLosses" && selectedMonth !== null
  );
  const title = l10n.getString("heading-net-worth-widget");

  // [] while loading/erroring would otherwise read as "none available".
  const canApplyPeriodHiding = !isTrendLoading && !trendError;

  const closeDrawer = () => setIsDrawerOpen(false);
  // Re-clicking an open drawer's trigger closes it - some drawers (e.g.
  // breakdown) have no other way to close in the legacy inline layout.
  const toggleDrawer = (kind: DrawerKind) => {
    if (isDrawerOpen && drawerKind === kind) {
      closeDrawer();
    } else {
      openDrawer(kind);
    }
  };

  let drawerContent: ReactNode;
  if (drawerKind === "filter") {
    drawerContent = (
      <AccountFilterPanel
        accounts={eligibleAccounts}
        selectedAccountIds={selectedAccountIds}
        isOpen={isDrawerOpen}
        onApply={(ids) => {
          setSelectedAccountIds(ids);
          closeDrawer();
        }}
        onDismiss={closeDrawer}
      />
    );
  } else if (drawerKind === "gainsLosses" && selectedMonth) {
    drawerContent = (
      <NetWorthGainsLosses
        month={selectedMonth}
        accounts={filteredAccounts}
        accountTrends={accountTrends}
        isLoading={isAccountTrendsLoading}
        hasError={hasAccountTrendsError}
      />
    );
  } else {
    drawerContent = (
      <NetWorthBreakdown
        accounts={filteredAccounts}
        totalAssets={totalAssets}
        totalLiabilities={totalLiabilities}
      />
    );
  }

  const isPositiveChange = (changeThisMonth ?? 0) >= 0;
  const isSmall = !m;

  const content = (
    <div>
      <div className={styles.topRow}>
        <div className={cc([styles.topRowSide, styles.topRowStart])}>
          <IconButton
            name="filter"
            onClick={() => toggleDrawer("filter")}
            disabled={isDrawerOpen || eligibleAccounts.length === 0}
            label={l10n.getString("net-worth-filter-trigger")}
            testId="net-worth-filter-toggle"
          />
          {!borderCard && <SectionCard.Title text={title} />}
        </div>
        <div className={styles.topRowCenter}>
          {!isSmall && (
            <NetWorthPeriodTabs
              period={period}
              availablePeriods={availablePeriods}
              canApplyHiding={canApplyPeriodHiding}
              onPeriodChange={setPeriod}
            />
          )}
        </div>
        <div className={styles.topRowSide} />
      </div>
      <div
        className={styles.amount}
        aria-live="polite"
        data-testid="net-worth-total"
      >
        {formatNumber(netWorth, "currency")}
      </div>
      {!isSmall &&
        !isTrendLoading &&
        !trendError &&
        changeThisMonth !== null && (
          <div
            data-testid="net-worth-change-this-month"
            className={cc([
              styles.changeThisMonth,
              isPositiveChange && styles.changeThisMonthPositive,
            ])}
          >
            <span
              className={cc([
                isPositiveChange
                  ? "narmi-icon-arrow-up-right"
                  : "narmi-icon-arrow-down-right",
                styles.changeIcon,
              ])}
            />
            <span className={styles.changeValue}>
              {formatNumber(Math.abs(changeThisMonth), "currency")}
            </span>
            {l10n.getString("label-net-worth-change-this-month")}
          </div>
        )}
      <NetWorthTrendChart
        period={period}
        chartData={chartData}
        windowStart={windowStart}
        windowEnd={windowEnd}
        isLoading={isTrendLoading}
        error={trendError}
        isSmall={isSmall}
        onSelectMonth={openGainsLosses}
      />
      <div className={styles.footerRow}>
        {externalTrackingStartedAt && (
          <div className={styles.captions}>
            <p
              data-testid="net-worth-tracking-caption"
              className={cc([styles.caption, "fontSize--xs"])}
            >
              {l10n.getString("caption-net-worth-tracking-started", {
                date: format(externalTrackingStartedAt, "MMM d, yyyy"),
              })}
            </p>
            <p
              data-testid="net-worth-external-lag-caption"
              className={cc([styles.caption, "fontSize--xs"])}
            >
              {l10n.getString("caption-net-worth-external-lag")}
            </p>
          </div>
        )}
        <Button
          kind="plain"
          size="s"
          endIcon="chevron-right"
          onClick={() => toggleDrawer("breakdown")}
          testId="net-worth-breakdown-toggle"
          className={styles.breakdownTrigger}
          label={l10n.getString("net-worth-breakdown-view")}
        />
      </div>
      {/* Without BorderCard's own drawer, show the same content inline. */}
      {!borderCard && isDrawerOpen && (
        <div className={styles.inlineDrawer}>{drawerContent}</div>
      )}
      <Alert
        isActive={hasDisconnectedExternalAccount}
        isDismissable={false}
        kind="warn"
        paddingSize="xxs"
      >
        {l10n.getString("warning-net-worth-external-disconnected")}
      </Alert>
    </div>
  );

  return borderCard ? (
    <BorderCard
      title={title}
      isDrawerOpen={isDrawerOpen}
      onDrawerDismiss={closeDrawer}
      drawerChildren={drawerContent}
    >
      {content}
    </BorderCard>
  ) : (
    <SectionCard classNames="dashboard-section-card">{content}</SectionCard>
  );
};

export default NetWorthWidgetCard;
